/**
 * Player actions as pure state transitions: fire cost, jump launch, move cost.
 * Previously inline in Game.fire / Game.executeJump / Game.handleMoveClick
 * (main.ts) next to the animation, audio and network code. The remote-peer
 * input path and the bot path can now share these instead of re-deriving them.
 */

import type { GameState, PlayerState, PlanetState, FireParams, WeaponKey, Vec3, ModeConfig, CarryableObjectState } from '../types';
import { WEAPONS, FIRE_MOVEMENT_COST, JUMP_MIN_COST, JUMP_VELOCITY_PER_MOVEMENT, JUMPPACK_VELOCITY_BONUS } from '../constants';
import { computeLaunchDirection, getPlayerWorldPosition, getPlayerSurfaceNormal } from './gameState';
import { vecNormalize, vecSlerp } from '../math';
import { hasUnlimitedJumpFuel } from './modeRules';
import { canCarrierJump, getCarrierMovementPenalty } from './carryables';
import { dacos } from './dmath';
import { completeFireMission } from './squads';

export type ActionCheck = { ok: true } | { ok: false; reason: string };

export interface AimParams {
  azimuth: number;
  elevation: number;
  power: number;
}

// =============================================================================
// FIRE
// =============================================================================

/**
 * I3 (the probe is the cheapest drone; decisions 25, 62): a probe shot flies on a carried drone, the drone's cost
 * (one per flight), when the unit has no probe rounds of its own (none listed counts as none, as the bots read it).
 * Units carry no drones unless a setup gives them some, so classic play is unchanged.
 */
export function probePaidByDrone(player: PlayerState, weaponKey: WeaponKey): boolean {
  if (weaponKey !== 'probe' || (player.drones ?? 0) <= 0) return false;
  const ammo = player.loadout.ammo.probe;
  return ammo !== null && (ammo === undefined || ammo <= 0);
}

export function checkFire(player: PlayerState, weaponKey: WeaponKey): ActionCheck {
  const weapon = WEAPONS[weaponKey];
  const ammo = player.loadout.ammo[weaponKey];
  if (probePaidByDrone(player, weaponKey)) {
    if (player.movementRemaining < FIRE_MOVEMENT_COST) return { ok: false, reason: `Not enough movement to fire! (need ${FIRE_MOVEMENT_COST}, have ${player.movementRemaining.toFixed(1)})` };
    return { ok: true };
  }
  if (ammo !== null && ammo !== undefined && ammo <= 0) {
    return { ok: false, reason: `Out of ${weapon.name} ammo!` };
  }
  if (player.movementRemaining < FIRE_MOVEMENT_COST) {
    return { ok: false, reason: `Not enough movement to fire! (need ${FIRE_MOVEMENT_COST}, have ${player.movementRemaining.toFixed(1)})` };
  }
  return { ok: true };
}

/** Count the shot, spend ammo (if finite) and movement, remember the aim. */
export function applyFireCost(state: GameState, playerId: number, params: FireParams): GameState {
  const players = state.players.map(p => {
    if (p.id !== playerId) return p;
    const ammo = { ...p.loadout.ammo };
    const onDrone = probePaidByDrone(p, params.weaponKey);
    if (!onDrone && ammo[params.weaponKey] !== null && ammo[params.weaponKey] !== undefined) {
      ammo[params.weaponKey] = (ammo[params.weaponKey] as number) - 1;
    }
    return {
      ...p,
      ...(onDrone ? { drones: (p.drones ?? 0) - 1, probePaidBy: 'drone' as const } : params.weaponKey === 'probe' ? { probePaidBy: undefined } : {}),
      shotsFired: p.shotsFired + 1,
      ...(params.weaponKey === 'probe' ? { probesFired: (p.probesFired ?? 0) + 1 } : {}),
      movementRemaining: p.movementRemaining - FIRE_MOVEMENT_COST,
      lastAimAzimuth: params.azimuth,
      lastAimElevation: params.elevation,
      lastAimPower: params.power,
      selectedWeapon: params.weaponKey,  // the weapon you fired is the one you start the next turn with
      loadout: { ...p.loadout, ammo },
    };
  });
  // §15.2: the shot answers the unit's fire mission (one per unit), paid here like every shot, queued ones included
  return completeFireMission({ ...state, players }, playerId);
}

/** Undo applyFireCost for a shot that was queued and then taken back (round flows). */
export function refundFireCost(state: GameState, playerId: number, params: FireParams): GameState {
  const players = state.players.map(p => {
    if (p.id !== playerId) return p;
    const ammo = { ...p.loadout.ammo };
    const onDrone = params.weaponKey === 'probe' && p.probePaidBy === 'drone';
    if (!onDrone && ammo[params.weaponKey] !== null && ammo[params.weaponKey] !== undefined) {
      ammo[params.weaponKey] = (ammo[params.weaponKey] as number) + 1;
    }
    return {
      ...p,
      ...(onDrone ? { drones: (p.drones ?? 0) + 1, probePaidBy: undefined } : {}),
      shotsFired: Math.max(0, p.shotsFired - 1),
      ...(params.weaponKey === 'probe' ? { probesFired: Math.max(0, (p.probesFired ?? 0) - 1) } : {}),
      movementRemaining: p.movementRemaining + FIRE_MOVEMENT_COST,
      loadout: { ...p.loadout, ammo },
    };
  });
  return { ...state, players };
}

// =============================================================================
// JUMP
// =============================================================================

export interface JumpLaunch {
  ok: true;
  launchPos: Vec3;
  launchVel: Vec3;
  movementToUse: number;
  burnFuel: boolean;
  unlimitedFuel: boolean;
}

export interface JumpVector {
  launchPos: Vec3;
  launchVel: Vec3;
  launchDir: Vec3;
  movementToUse: number;
  burnFuel: boolean;
  unlimitedFuel: boolean;
}

/**
 * Launch point and velocity for a jump, with no feasibility checks (the jump
 * preview draws this even when the jump would be refused). Power is the
 * fraction of remaining movement to spend; velocity scales with movement
 * spent plus the jumppack bonus (fuel is unlimited outside deathmatch) plus
 * the player's upgrade jumpBonus.
 */
/** The unit's extra jump velocity from its kit (thrusters, jump trait) — the legacy `jumpBonus` when no modifiers were resolved. */
export function jumpVelocityBonusOf(player: Pick<PlayerState, 'modifiers' | 'jumpBonus'>): number {
  return player.modifiers?.jumpVelocityBonus ?? player.jumpBonus ?? 0;
}

export function jumpLaunchVector(player: PlayerState, planets: PlanetState[], aim: AimParams, modeConfig: ModeConfig): JumpVector {
  const movementToUse = (aim.power / 100) * player.movementRemaining;
  const playerPos = getPlayerWorldPosition(player, planets);
  const surfaceNormal = getPlayerSurfaceNormal(player);
  const launchDir = computeLaunchDirection(surfaceNormal, aim.azimuth, aim.elevation);

  const unlimitedFuel = hasUnlimitedJumpFuel(modeConfig);
  const burnFuel = unlimitedFuel || player.jumppackFuel > 0;
  let velocity = movementToUse * JUMP_VELOCITY_PER_MOVEMENT;
  if (burnFuel) velocity += JUMPPACK_VELOCITY_BONUS;
  velocity += jumpVelocityBonusOf(player);

  return {
    movementToUse, burnFuel, unlimitedFuel, launchDir,
    launchVel: { x: launchDir.x * velocity, y: launchDir.y * velocity, z: launchDir.z * velocity },
    launchPos: {
      x: playerPos.x + surfaceNormal.x * 0.5,
      y: playerPos.y + surfaceNormal.y * 0.5,
      z: playerPos.z + surfaceNormal.z * 0.5,
    },
  };
}

/** Jump feasibility (carrying, minimum power) + launch vector. */
export function computeJumpLaunch(
  player: PlayerState,
  planets: PlanetState[],
  aim: AimParams,
  modeConfig: ModeConfig,
  carryables: CarryableObjectState[],
): JumpLaunch | { ok: false; reason: string } {
  if (!canCarrierJump(player.id, carryables, modeConfig)) {
    return { ok: false, reason: "Can't jump while carrying an object!" };
  }
  if (player.tetheredTurns && player.tetheredTurns > 0) {
    return { ok: false, reason: "Can't jump while tethered!" };
  }
  const v = jumpLaunchVector(player, planets, aim, modeConfig);
  if (v.movementToUse < JUMP_MIN_COST) {
    return { ok: false, reason: 'Not enough power! Increase power slider.' };
  }
  return { ok: true, ...v };
}

/** Put the player in the air with the computed launch, spending movement and (finite) fuel. */
export function applyJump(state: GameState, playerId: number, launch: JumpLaunch): GameState {
  const players = state.players.map(p => (p.id !== playerId ? p : {
    ...p,
    airborne: true,
    airbornePos: launch.launchPos,
    airborneVel: launch.launchVel,
    airborneCause: 'jump' as const,
    movementRemaining: p.movementRemaining - launch.movementToUse,
    jumppackFuel: (launch.burnFuel && !launch.unlimitedFuel) ? p.jumppackFuel - 1 : p.jumppackFuel,
  }));
  return { ...state, players };
}

// =============================================================================
// MOVE
// =============================================================================

export interface MoveCost {
  distance: number;        // arc length along the surface
  effectiveCost: number;   // movement points, after the carrier penalty
  affordable: boolean;
  /** Set when the move is refused for a reason other than distance (tethered). */
  blocked?: string;
}

export function computeMoveCost(
  player: PlayerState,
  planet: PlanetState,
  targetDir: Vec3,
  carryables: CarryableObjectState[],
  modeConfig: ModeConfig,
): MoveCost {
  const c = player.surfaceDir;
  const d = c.x * targetDir.x + c.y * targetDir.y + c.z * targetDir.z;
  const angle = dacos(Math.max(-1, Math.min(1, d)));
  const distance = angle * planet.radius;
  const penalty = getCarrierMovementPenalty(player.id, carryables, modeConfig);
  const effectiveCost = penalty > 0 ? distance / (1 - penalty) : distance;
  if (player.tetheredTurns && player.tetheredTurns > 0) {
    return { distance, effectiveCost, affordable: false, blocked: "Can't move while tethered!" };
  }
  return { distance, effectiveCost, affordable: effectiveCost <= player.movementRemaining };
}

/**
 * Clamp a desired surface destination to what the player can afford this turn.
 * Returns the (possibly shortened) target direction, or null when the walk
 * would be too short to matter. The result is always affordable for
 * `computeMoveCost`, with a tiny epsilon so slerp→arc-distance rounding can
 * never push it over the budget. Used by bots, which plan a destination and
 * walk as far toward it as they can.
 */
export function clampMoveTarget(
  player: PlayerState,
  planet: PlanetState,
  desiredDir: Vec3,
  carryables: CarryableObjectState[],
  modeConfig: ModeConfig,
): Vec3 | null {
  const currentDir = vecNormalize(player.surfaceDir);
  const targetDir = vecNormalize(desiredDir);
  const d = currentDir.x * targetDir.x + currentDir.y * targetDir.y + currentDir.z * targetDir.z;
  const dist = dacos(Math.max(-1, Math.min(1, d))) * planet.radius;
  const penalty = getCarrierMovementPenalty(player.id, carryables, modeConfig);
  const rawMax = penalty > 0 ? player.movementRemaining * (1 - penalty) : player.movementRemaining;
  const walkDist = Math.min(rawMax - 0.001, dist);
  if (walkDist <= 0.01) return null;
  const t = dist > 0.001 ? Math.min(1, walkDist / dist) : 1;
  return t >= 1 ? targetDir : vecNormalize(vecSlerp(currentDir, targetDir, t));
}

// =============================================================================
// PLANNED MOVEMENT (round flows: the move is charged now and walked in the round)
// =============================================================================

/** Deduct (or, negative, refund) movement points without moving the unit. */
export function spendMovement(state: GameState, playerId: number, amount: number): GameState {
  return { ...state, players: state.players.map(p => p.id !== playerId ? p : { ...p, movementRemaining: p.movementRemaining - amount }) };
}

/** Charge a jump's movement and fuel without leaving the ground — the round simulator launches it. */
export function chargeJump(state: GameState, playerId: number, launch: JumpLaunch): GameState {
  return { ...state, players: state.players.map(p => p.id !== playerId ? p : {
    ...p,
    movementRemaining: p.movementRemaining - launch.movementToUse,
    jumppackFuel: (launch.burnFuel && !launch.unlimitedFuel) ? p.jumppackFuel - 1 : p.jumppackFuel,
  }) };
}
